// Home (DESIGN §2): the title line, Open a song, the Rhythm Lab, Your songs (the device library), and three small links.
// First run (no songs yet, hint not dismissed): the Rhythm Lab leads, with a three-step hint.

import { useEffect, useState } from 'preact/hooks';
import { sizeWords } from '../diagnostics.js';
import { engine } from '../engine/engine.js';
import { openLab, openSong } from './actions.js';
import { Switch } from './controls.js';
import { useEngineState } from './engine-state.js';
import { exportAllCounts, pickSong } from './files.js';
import { finishFirstRun, firstRunDone } from './firstrun.js';
import { copyOf } from './keep.js';
import { forgetSong, listSongs, removeSong, spaceUsed, type SongRecord } from './library.js';
import { Sheet } from './sheet.js';
import { Toast } from './toast.js';
import { shareApp } from './share.js';
import { HomeByline, SponsorCard } from './sponsor-ui.js';
import { reloadForUpdate } from '../sw-register.js';
import { closeSheet, enterPlayer, openSheet, setKeep, ui } from './ui.js';

export function UpdateNotice() {
  const { update } = ui.use();
  if (!update) return null;
  return (
    <p class="update" role="status" data-testid="update-notice">
      New version ready · <button type="button" class="link" onClick={reloadForUpdate}>Reload</button>
    </p>
  );
}

export function Home() {
  const s = useEngineState();
  const [songs, setSongs] = useState<SongRecord[] | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [hintDone, setHintDone] = useState(firstRunDone());
  const { sheet, keep } = ui.use();

  // Re-read the list on arrival and after a sheet closes (a removal).
  useEffect(() => {
    let live = true;
    void listSongs().then((list) => { if (live) setSongs(list); });
    return () => { live = false; };
  }, [sheet === null]);

  // Drag and drop (desktop): a song dropped anywhere on Home opens.
  useEffect(() => {
    const over = (e: DragEvent) => { if (e.dataTransfer?.types.includes('Files')) { e.preventDefault(); setDragging(true); } };
    const leave = (e: DragEvent) => { if (e.relatedTarget === null) setDragging(false); };
    const drop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer?.files[0];
      if (file) void openSong(file);
    };
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
    };
  }, []);

  const open = s.source !== 'none' ? s.track : null;
  const first = songs !== null && songs.length === 0 && !hintDone;

  async function choose(song: SongRecord) {
    if (s.source === 'file' && s.track?.id === song.id) { enterPlayer(); return; }
    if (song.kept) {
      const file = await copyOf(song);
      if (file) { void openSong(file, { fromCopy: true }); return; }
      setSongs((list) => list && list.map((x) => (x.id === song.id ? { ...x, kept: false } : x)));
    }
    // No copy on this device: the file is asked for again; its taps come back with it.
    setAsking(song.name);
    pickSong();
  }

  const labCard = (
    <button type="button" class={`card${first ? ' lead' : ''}`} data-testid="lab-card" aria-labelledby="lab-card-title" aria-describedby="lab-card-line" onClick={() => void openLab()}>
      <span class="card-title" id="lab-card-title">Rhythm Lab</span>
      <span class="card-line" id="lab-card-line">No song? Start here.</span>
    </button>
  );

  const kept = (songs ?? []).filter((x) => x.kept);

  return (
    <main class="screen home" data-dragging={dragging ? '' : undefined}>
      <header class="home-head">
        <h1>salsacount</h1>
        <div class="tagline-row">
          <p class="tagline">See the count. Hear the count.</p>
          <HomeByline />
        </div>
      </header>
      <UpdateNotice />

      {first && labCard}
      {first && (
        <section class="firstrun" aria-labelledby="firstrun-heading" data-testid="first-run">
          <h2 id="firstrun-heading">Three steps</h2>
          <ol class="steps">
            <li><span class="step-n" aria-hidden="true">1</span>Open a song</li>
            <li><span class="step-n" aria-hidden="true">2</span>Tap on 1 twice</li>
            <li><span class="step-n" aria-hidden="true">3</span>Dance</li>
          </ol>
          <button type="button" class="btn" onClick={() => { finishFirstRun(); setHintDone(true); }}>Got it</button>
        </section>
      )}

      <button type="button" class="btn primary open" onClick={pickSong}>Open a song</button>
      <p class="drop-hint">{dragging ? 'Drop the song to open it.' : 'Or drop a song here.'}</p>
      {asking && <p class="note" role="status">Choose “{asking}” again. Your taps come back with it.</p>}

      {!first && labCard}

      {open && (
        <button type="button" class="card quiet-card" onClick={enterPlayer}>
          <span class="card-title">Back to {open.name}</span>
          <span class="card-line">Where you left it.</span>
        </button>
      )}

      <section class="songs-section" aria-labelledby="songs-heading">
        <h2 id="songs-heading">Your songs</h2>
        {songs === null || songs.length === 0 ? (
          <p class="note">{songs === null ? '' : 'Songs you count will be listed here.'}</p>
        ) : (
          <ul class="songs" data-testid="songs">
            {songs.map((song) => (
              <li key={song.id}>
                <button type="button" class="song" onClick={() => void choose(song)}>
                  <span class="song-name">{song.name}</span>
                  <span class="song-state" data-counted={song.counted ?? song.taps >= 2 ? '' : undefined}>
                    {song.counted ?? song.taps >= 2 ? 'Counted' : 'Needs taps'}{song.kept ? ' · Kept on this device' : ''}
                  </span>
                </button>
                <button type="button" class="btn icon" aria-label={`Options for ${song.name}`} aria-haspopup="dialog" onClick={() => ui.patch({ sheet: 'song', songId: song.id })}>
                  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Switch
          label="Keep on this device"
          hint={keep ? 'A copy of each song you open is kept, so one tap reopens it.' : 'Off: you choose the file each time.'}
          checked={keep}
          onChange={setKeep}
        />
        {kept.length > 0 && (
          <p class="note" data-testid="space-used">Space used: {sizeWords(spaceUsed(kept))} for {kept.length} {kept.length === 1 ? 'song' : 'songs'}.</p>
        )}
      </section>

      <SponsorCard />

      <div class="home-toast"><Toast /></div>

      <nav class="links" aria-label="More">
        <button type="button" class="link" onClick={() => openSheet('calibrate')}>Calibrate</button>
        <button type="button" class="link" onClick={() => openSheet('diagnostics')}>Diagnostics</button>
        <button type="button" class="link" onClick={() => openSheet('about')}>About</button>
        <button type="button" class="link" onClick={() => void shareApp()}>Share</button>
      </nav>
      <p class="links-more"><button type="button" class="link" data-testid="export-all" onClick={() => void exportAllCounts()}>Export all my counts</button></p>
    </main>
  );
}

/** The menu of one song on Home: remove its copy from this device and, if the dancer says so, its taps. */
export function SongSheet() {
  const { songId } = ui.use();
  const [song, setSong] = useState<SongRecord | null>(null);
  const [step, setStep] = useState<'menu' | 'taps' | 'done'>('menu');
  const [problem, setProblem] = useState('');
  useEffect(() => {
    void listSongs().then((l) => setSong(l.find((x) => x.id === songId) ?? null));
  }, [songId]);
  const name = song?.name ?? 'this song';

  async function remove() {
    if (!songId) return;
    setProblem('');
    let ok = false;
    try { ok = await removeSong(songId); } catch { /* handled below */ }
    if (!ok) { setProblem('The copy could not be removed from this browser. Try again, or clear this site\'s data in the browser settings.'); return; }
    setStep('taps');
  }

  async function deleteTaps() {
    if (!songId) return;
    try {
      await forgetSong(songId);
      if (engine.getState().track?.id === songId) engine.close();
      setStep('done');
    } catch {
      setProblem('The taps could not be deleted. Try again.');
    }
  }

  if (step === 'taps') {
    return (
      <Sheet title={name}>
        <p class="sentence" role="status">{song?.kept ? 'The copy of the song is removed from this device.' : 'The song is off your list.'}</p>
        <p class="sentence">Also delete the taps and fixes you saved for it, and any truth taps? They are the only record of your count, and they cannot be brought back.</p>
        {problem && <p class="sentence" role="alert">{problem}</p>}
        <button type="button" class="btn primary" data-testid="delete-taps" onClick={() => void deleteTaps()}>Delete the taps too</button>
        <button type="button" class="btn" data-testid="keep-taps" onClick={() => closeSheet()}>Keep my taps</button>
        <p class="note">If you keep them, open the song file again any time and they come back.</p>
      </Sheet>
    );
  }
  if (step === 'done') {
    return (
      <Sheet title={name}>
        <p class="sentence" role="status">Removed, with its taps.</p>
        <button type="button" class="btn" onClick={() => closeSheet()}>Close</button>
      </Sheet>
    );
  }
  return (
    <Sheet title={name}>
      <p class="sentence">
        {song?.kept ? `A copy of this song is kept on this device (${sizeWords(song.bytes ?? 0)}).` : 'This song is not kept on this device: you choose the file each time.'}
      </p>
      <p class="note">{song?.kept ? 'Removing deletes that copy. You will be asked about the taps next. The song file you opened it from is not touched.' : 'Removing takes it off the list. You will be asked about the taps next.'}</p>
      {problem && <p class="sentence" role="alert">{problem}</p>}
      <button type="button" class="btn primary" data-testid="remove-song" onClick={() => void remove()}>
        {song?.kept ? 'Remove from this device' : 'Remove from the list'}
      </button>
    </Sheet>
  );
}
